import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Attachment } from 'fhir/r4';
import { UploadUiComponent } from '../../upload-ui/upload-ui.component';

@Component({
  selector: 'app-attachment-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, UploadUiComponent],
  templateUrl: './attachment-editor.component.html',
  styleUrl: './attachment-editor.component.scss'
})
export class AttachmentEditorComponent implements OnChanges {
  @Input() attachments: Attachment[] = [];
  @Input() allowUpload = true;
  @Input() allowManual = true;
  @Input() maxFiles = 5;
  @Input() allowedFiles: RegExp = /.*/;
  @Input() allowedFilesCategoryLabel = 'File';
  @Input() cloudName?: string;
  @Input() uploadPreset?: string;

  @Output() attachmentsChange = new EventEmitter<Attachment[]>();

  uploadUiFiles: any[] = [];
  editableAttachments: Attachment[] = [];

  ngOnChanges(): void {
    this.syncFromInput();
  }

  onUploadFilesChanged(files: any[]) {
    this.uploadUiFiles = Array.isArray(files) ? files : [];
    const uploaded = this.uploadUiFiles.map(file => this.mapUploadFileToAttachment(file));
    const manual = this.editableAttachments.filter(att => !att.url);
    this.editableAttachments = [...uploaded, ...manual];
    this.emitCombined();
  }

  addManualAttachment() {
    this.editableAttachments = [
      ...this.editableAttachments,
      { title: '', url: '', contentType: '', creation: '' }
    ];
  }

  removeAttachment(index: number) {
    this.editableAttachments.splice(index, 1);
    this.editableAttachments = [...this.editableAttachments];
    this.emitCombined();
  }

  isImage(att: Attachment): boolean {
    if (att.contentType && att.contentType.startsWith('image/')) return true;
    const url = att.url || '';
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url);
  }

  trackByIndex(index: number): number {
    return index;
  }

  private syncFromInput() {
    const safe = Array.isArray(this.attachments) ? this.attachments : [];
    this.editableAttachments = safe.map(att => ({ ...att }));
    this.uploadUiFiles = safe
      .filter(att => !!att.url)
      .map(att => this.mapAttachmentToUploadFile(att));
  }

  emitCombined() {
    const cleaned = this.editableAttachments.filter(att => {
      const hasContent = !!(att.url || att.data || att.title || att.contentType || att.creation || att.size);
      return hasContent;
    });
    this.attachmentsChange.emit(cleaned.map(att => ({ ...att })));
  }

  private mapUploadFileToAttachment(file: any): Attachment {
    const creation = this.normalizeDate(file?.uploadDate || file?.creation || file?.uploadedOn);
    return {
      title: file?.uploadFileName || this.filenameFromUrl(file?.fileLink) || undefined,
      url: file?.fileLink || undefined,
      contentType: file?.uploadFileType || undefined,
      size: file?.uploadFileSize,
      creation
    };
  }

  private mapAttachmentToUploadFile(att: Attachment): any {
    return {
      fileLink: att.url,
      uploadFileName: att.title || this.filenameFromUrl(att.url) || 'Attachment',
      uploadFileType: att.contentType || '',
      uploadFileSize: att.size,
      uploadDate: att.creation
    };
  }

  private normalizeDate(raw: any): string | undefined {
    if (!raw) return undefined;
    if (typeof raw === 'string') return raw.split('T')[0];
    try {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return undefined;
      return d.toISOString().split('T')[0];
    } catch {
      return undefined;
    }
  }

  private filenameFromUrl(url?: string): string | undefined {
    if (!url) return undefined;
    const part = url.split('/').pop();
    return part ? decodeURIComponent(part.split('?')[0]) : undefined;
  }
}
